import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/authService';
import {
  getCalendarDaysUntil,
  parseLocalDateOnly,
  toLocalDateString,
} from '../utils/localDate';

/**
 * Upcoming consultation countdown + 5-day booking reminders for vendor home/dashboard.
 */
export default function useVendorScheduleAlerts() {
  const [showEventReminderModal, setShowEventReminderModal] = useState(false);

  const { data: pendingConsultationsData } = useQuery({
    queryKey: ['vendorConsultations', 'pending'],
    queryFn: async () => {
      const response = await api.get('/vendor/consultations', {
        params: { status: 'pending' },
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const pendingConsultations =
    pendingConsultationsData?.data?.data || pendingConsultationsData?.data || [];
  const pendingConsultationCount = pendingConsultations.length;

  const { data: acceptedConsultationsData } = useQuery({
    queryKey: ['vendorConsultations', 'meeting_scheduled'],
    queryFn: async () => {
      const response = await api.get('/vendor/consultations', {
        params: { status: 'meeting_scheduled', per_page: 10 },
      });
      return response.data;
    },
    staleTime: 60 * 1000,
  });

  const acceptedConsultationsRaw =
    acceptedConsultationsData?.data?.data || acceptedConsultationsData?.data || [];

  const upcomingConsultation = useMemo(() => {
    if (!acceptedConsultationsRaw?.length) return null;
    const now = new Date();

    const withDates = acceptedConsultationsRaw
      .map((c) => {
        if (!c.scheduled_date) return null;
        const base = parseLocalDateOnly(c.scheduled_date);
        if (!base) return null;
        if (c.scheduled_time) {
          const [hRaw, mRaw] = String(c.scheduled_time).split(':');
          base.setHours(Number(hRaw || 0), Number(mRaw || 0), 0, 0);
        }
        if (base <= now) return null;
        return { ...c, _targetDateTime: base };
      })
      .filter(Boolean);

    if (withDates.length === 0) return null;
    withDates.sort((a, b) => a._targetDateTime - b._targetDateTime);
    return withDates[0];
  }, [acceptedConsultationsRaw]);

  const reminderRange = useMemo(() => {
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + 5);
    return {
      start: toLocalDateString(today),
      end: toLocalDateString(end),
    };
  }, []);

  const { data: eventReminderData } = useQuery({
    queryKey: ['vendorBookings', 'eventReminder', reminderRange.start, reminderRange.end],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings', {
        params: {
          start_date: reminderRange.start,
          end_date: reminderRange.end,
          per_page: 50,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const eventReminderBookingsRaw = (
    eventReminderData?.data?.data ||
    eventReminderData?.data ||
    []
  ).filter((b) => b.status !== 'cancelled');

  const eventReminderByDays = useMemo(() => {
    return eventReminderBookingsRaw.reduce((acc, b) => {
      const days = getCalendarDaysUntil(b.event_date);
      if (days != null && days >= 0 && days <= 5) {
        if (!acc[days]) acc[days] = [];
        acc[days].push(b);
      }
      return acc;
    }, {});
  }, [eventReminderBookingsRaw]);

  const fiveDayReminderBookings = useMemo(
    () => Object.values(eventReminderByDays).flat(),
    [eventReminderByDays]
  );

  useEffect(() => {
    if (fiveDayReminderBookings.length === 0) return;
    const today = new Date().toDateString();
    const storageKey = `fiveDayReminderModal_${today}`;
    if (!localStorage.getItem(storageKey)) {
      setShowEventReminderModal(true);
    }
  }, [fiveDayReminderBookings.length]);

  const handleCloseEventReminderModal = () => {
    const today = new Date().toDateString();
    localStorage.setItem(`fiveDayReminderModal_${today}`, '1');
    setShowEventReminderModal(false);
  };

  return {
    upcomingConsultation,
    pendingConsultationCount,
    fiveDayReminderBookings,
    eventReminderByDays,
    showEventReminderModal,
    setShowEventReminderModal,
    handleCloseEventReminderModal,
  };
}
